'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2, User, Search, Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/use-debounce';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { operatorQueries, type Operator } from '@/features/operators';
import {
  formatDate,
  getAvatarColor,
  getInitials
} from '../utils/operator-avatar';

interface OperatorSelectionPanelProps {
  selectedOperatorId: number | null;
  onSelectOperator: (id: number, name: string) => void;
}

export function OperatorSelectionPanel({
  selectedOperatorId,
  onSelectOperator
}: OperatorSelectionPanelProps) {
  const t = useTranslations('handoverScanning');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const {
    items: operators,
    isLoading,
    isFetchingNextPage,
    loadMoreRef
  } = useInfiniteScroll<Operator>(
    operatorQueries.infiniteList({
      availability: ['available'],
      ...(debouncedSearch ? { search: debouncedSearch } : {})
    })
  );

  return (
    <Card className='flex h-full flex-col'>
      <CardHeader className='space-y-3 pb-3'>
        <div className='flex items-center gap-2'>
          <Users className='text-muted-foreground h-5 w-5' />
          <CardTitle className='text-base'>
            {t('panelAssignOperator')}
          </CardTitle>
        </div>

        {/* Search */}
        <div className='relative'>
          <Search className='text-muted-foreground absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2' />
          <Input
            placeholder={t('searchOperators')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className='ps-9'
          />
        </div>
      </CardHeader>

      <CardContent
        className='flex-1 overflow-y-auto pb-4'
        style={{ maxHeight: 'calc(100vh - 350px)' }}
      >
        {isLoading ? (
          <div className='space-y-3 pt-3'>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className='h-24 w-full rounded-lg' />
            ))}
          </div>
        ) : operators.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-12 text-center'>
            <Users className='text-muted-foreground mb-3 h-10 w-10' />
            <p className='text-muted-foreground text-sm'>
              {t('noOperatorsFound')}
            </p>
          </div>
        ) : (
          <div className='space-y-3 pt-3'>
            {operators.map((operator) => {
              const isSelected = selectedOperatorId === operator.id;
              const name = operator.name ?? '';

              return (
                <button
                  key={operator.id}
                  type='button'
                  onClick={() => onSelectOperator(operator.id, name)}
                  className={cn(
                    'w-full rounded-lg border p-3 text-start transition-all',
                    'hover:border-primary/50 hover:bg-primary/5',
                    isSelected && 'ring-primary bg-primary/5 ring-2'
                  )}
                >
                  <div className='flex items-start gap-3'>
                    {/* Avatar */}
                    <div
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white',
                        name
                          ? getAvatarColor(operator.id)
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {name ? getInitials(name) : <User className='h-4 w-4' />}
                    </div>

                    <div className='min-w-0 flex-1 space-y-1'>
                      {/* Name + workload summary */}
                      <div className='flex flex-wrap items-center justify-between gap-2'>
                        <p className='text-sm font-semibold'>{name || '—'}</p>
                        <span className='text-muted-foreground text-xs'>
                          {t('operatorWorkloadSummary', {
                            containers: operator.pending_containers_count,
                            pages: operator.total_pages_count
                          })}
                        </span>
                      </div>

                      {/* Employee ID + joined date */}
                      <div className='flex flex-wrap items-center gap-3 text-xs'>
                        {operator.employee_id && (
                          <span className='text-muted-foreground'>
                            {t('employeeId', {
                              id: operator.employee_id
                            })}
                          </span>
                        )}
                        <span className='text-muted-foreground ms-auto'>
                          {t('joinedDate', {
                            date: formatDate(operator.created_at)
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}

            <div ref={loadMoreRef} className='h-px' />
            {isFetchingNextPage && (
              <div className='flex items-center justify-center py-2'>
                <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
